import { defineStore } from 'pinia';
import { useSettlementSheetStore, type SettlementSheet } from '@/stores/settlement-sheet.ts';
import { useFormsStore } from 'vuniform';
import type { Event } from '@/generated/graphql';
import { formIds } from '@/helpers/formIds';

interface SettlementSheetFormState {
  selectedEventIds: number[];
}

interface TicketForm {
  _formId: string;
  total: number;
}

export type FormId = string;

const manualTicketFormIdPrefix = 'manual-ticket-row-form';
const customExpenseFormIdPrefix = 'custom-expense-row-form';

const getManualTicketFormKey = (k: unknown): string => `${manualTicketFormIdPrefix}-${k}`;
const getCustomExpenseFormKey = (k: unknown): string => `${customExpenseFormIdPrefix}-${k}`;

export const useSettlementSheetFormStore = defineStore('offerSheetForm', {
  state: (): SettlementSheetFormState => ({
    selectedEventIds: [],
  }),

  getters: {
    manualTicketFormIds(_: SettlementSheetFormState): string[] {
      return formIds(getManualTicketFormKey);
    },

    manualTicketsRows(): TicketForm[] {
      const formsStore = useFormsStore();
      return this.manualTicketFormIds.map(k => {
        const price = Number.parseFloat(<string>formsStore.fieldGetValue(k, 'price'));
        const ticketsSold = Number.parseInt(<string>formsStore.fieldGetValue(k, 'ticketsSold'));
        return {
          _formId: k,
          total: price * ticketsSold,
        };
      });
    },

    allDatesSelected(state: SettlementSheetFormState) {
      return state.selectedEventIds.length === useSettlementSheetStore().settlementSheet?.eventIds.length;
    },

    isEventSelected:
      (state: SettlementSheetFormState) =>
        (event: Event): boolean => {
          return state.selectedEventIds.indexOf(event.id) !== -1;
        },

    customExpenseFormIds(_: SettlementSheetFormState): string[] {
      return formIds(getCustomExpenseFormKey);
    },
  },

  actions: {
    initFromSettlementSheet() {
      this.$reset();
      const settlementSheetStore = useSettlementSheetStore();
      const settlementSheet: SettlementSheet | undefined = settlementSheetStore.settlementSheet;
      if (!settlementSheet) {
        return;
      }

      this.selectedEventIds = [...settlementSheet.eventIds];
    },

    toggleEventSelected(eventId: number, selected?: boolean) {
      const idx = this.selectedEventIds.indexOf(eventId);
      if (idx === -1) {
        if (selected !== false) {
          this.selectedEventIds.push(eventId);
        }
        return;
      }

      if (selected !== true) {
        this.selectedEventIds.splice(idx, 1);
      }
    },

    setAllDatesSelected(setSelected: boolean) {
      const settlementSheetStore = useSettlementSheetStore();
      const settlementSheet: SettlementSheet | undefined = settlementSheetStore.settlementSheet;

      for (const event of settlementSheet.availableEvents) {
        this.toggleEventSelected(event.id, setSelected);
      }
      settlementSheetStore.updateSelectedEvents(this.selectedEventIds);
    },

    deleteManualTicketRow(formId: string) {
      const formsStore = useFormsStore();
      formsStore.SET_INITIAL_VALUES({ formId, values: {}, clearDirty: true });
      formsStore.RESET_FIELD_VALUES(formId);
    },

    deleteCustomExpenseRow(formId: string) {
      const formsStore = useFormsStore();
      formsStore.SET_INITIAL_VALUES({ formId, values: {}, clearDirty: true });
      formsStore.RESET_FIELD_VALUES(formId);
    },
  },
});
